import type { HttpErrorResponse } from '@angular/common/http';

import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { of, Subscription } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  ISortEventArgs,
  TSortDirections,
} from 'src/app/shared/classes/grid-page-info';
import {
  GroupEnrollmentFilters,
  GroupEnrollmentStatuses,
  IGQLGroupEnrollmentParams,
  IGroupEnrollment,
  type IGroupEnrollmentUpdate,
} from 'src/app/shared/interfaces/group-enrollment/group-enrollment.interface';
import { AlertService } from 'src/app/shared/services/alert.service';
import { GroupEnrollmentStateService } from 'src/app/shared/services/group-enrollment/insurance/group-enrollment-state.service';
import { GroupEnrollmentGQLService } from 'src/app/shared/services/group-enrollment/insurance/group-enrollment.graphql.service';
import { GroupEnrollmentService } from 'src/app/shared/services/group-enrollment/insurance/group-enrollment.service';
import { UsersService } from 'src/app/shared/services/users/users.service';
import { DEFAULT_MODAL_OPTIONS } from 'src/app/shared/variables/variables';

import { FilterItems, IFilterItem } from '../../filters/filter.interface';
import { AddGroupEnrollmentsModalComponent } from '../add-group-enrollments-modal/add-group-enrollments-modal.component';
import { AddIndependantGroupEnrollmentsModalComponent } from '../add-independant-group-enrollments-modal/add-independant-group-enrollments-modal.component';
import { EditGroupEnrollmentCasenumberComponent } from '../edit-group-enrollment-casenumber/edit-group-enrollment-casenumber.component';
import { EditGroupEnrollmentNoteModalComponent } from '../edit-group-enrollment-note-modal/edit-group-enrollment-note-modal.component';
import { ViewModifyEnrollmentsModalComponent } from '../view-modify-enrollments-modal/view-modify-enrollments-modal.component';

@Component({
  selector: 'groups',
  templateUrl: './group-enrollments.component.html',
  styles: [':host { height: 100%; }'],
})
export class GroupEnrollmentsComponent implements OnInit, OnDestroy {
  @Input() groupId: string;
  @Input() isReadOnly = false;

  groupEnrollments: IGroupEnrollment[];
  groupEnrollmentsFiltred: IGroupEnrollmentDTO[];

  groupEnrollmentFilters: FilterItems;
  filterValues?: GroupEnrollmentFilters;
  filterAreas =
    "'dates dates . . . . . .' 'enrollmentStatus groupId npi taxId payerId payer payer routingId' 'show show enrollmentType caseNumber . . . btn'";

  groupEnrollmentStatuses = Object.keys(GroupEnrollmentStatuses);
  isFiltersExpanded: boolean;
  isLoading: boolean;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private modal: NgbModal,
    private alertService: AlertService,
    public groupEnrollmentStateService: GroupEnrollmentStateService,
    private groupEnrollmentGQLService: GroupEnrollmentGQLService,
    private groupEnrollmentService: GroupEnrollmentService,
    private usersService: UsersService,
  ) {
    this.isFiltersExpanded =
      !this.groupEnrollmentStateService.isFiltersExpanded;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit() {
    this.groupEnrollmentFilters =
      this.groupEnrollmentStateService.groupEnrollmentFilters;
    this.filterValues = this.groupEnrollmentStateService.filter;

    if (this.groupId) {
      const idFilter = this.groupEnrollmentFilters.items?.find((f) => {
        return (f as IFilterItem).name === 'id';
      });

      if (idFilter) {
        idFilter.disabled = true;
      }
      if (this.filterValues) {
        this.filterValues.id = this.groupId;
      }

      this.isLoading = true;

      this.groupEnrollmentGQLService
        .getGroupEnrollments({
          filter: { id: this.groupId },
          orderby: 'dateSetup',
          direction: 'ASC',
        })
        .pipe(
          map((result) => {
            const groupEnrollments: IGroupEnrollmentDTO[] = result.nodes.map(
              (x) => {
                return {
                  ...x,
                  isExpanded: true,
                };
              },
            );

            return {
              groupEnrollments,
              pageInfo: result.pageInfo,
            };
          }),
          catchError((error: unknown) => {
            if (error) {
              this.alertService.error(error as HttpErrorResponse | string);
            }
            return of(null);
          }),
        )
        .subscribe((result) => {
          if (result) {
            this.groupEnrollments.push(...result.groupEnrollments);

            this.groupEnrollmentStateService.groupEnrollments =
              this.groupEnrollments;

            this.groupEnrollmentStateService.updatePage(result.pageInfo);

            this.groupEnrollmentsFiltred = this.getGroupEnrollmentFiltered(
              this.groupEnrollments.map((x) => {
                return {
                  ...x,
                  isExpanded: true,
                };
              }),
            );
          } else {
            this.groupEnrollmentStateService.stopLoading();
          }

          this.isLoading = false;
        });
    }

    this.subscriptions.add(
      this.groupEnrollmentStateService.params$
        .pipe(
          map((params) => {
            this.isLoading = true;
            const gqlParams = params;

            if (this.groupEnrollmentStateService.isLoading) {
              this.groupEnrollments = [];
              this.groupEnrollmentsFiltred = [];
            }

            return gqlParams;
          }),
          switchMap((params) => {
            return this.getGroupEnrollments(
              params as IGQLGroupEnrollmentParams,
            );
          }),
        )
        .subscribe((result) => {
          if (result) {
            this.groupEnrollments.push(...result.groupEnrollments);
            this.groupEnrollmentStateService.groupEnrollments =
              this.groupEnrollments;

            this.groupEnrollmentStateService.updatePage(result.pageInfo);

            this.groupEnrollmentsFiltred = this.getGroupEnrollmentFiltered(
              this.groupEnrollments.map((x) => {
                return {
                  ...x,
                  isExpanded: true,
                };
              }),
            );
          } else {
            this.groupEnrollmentStateService.stopLoading();
          }

          this.isLoading = false;
        }),
    );

    this.groupEnrollments = this.groupEnrollmentStateService.groupEnrollments;
    this.isFiltersExpanded = this.isReadOnly ? false : this.isFiltersExpanded;
  }

  string_of_enum(value) {
    for (const k in GroupEnrollmentStatuses) {
      if (k == value) {
        return GroupEnrollmentStatuses[k];
      }
    }
    return null;
  }

  private getGroupEnrollments(params: IGQLGroupEnrollmentParams) {
    const { filter } = params;

    const filteredUserGroupsIdList: string[] = [];
    if (filter.id) {
      filteredUserGroupsIdList.push(filter.id);
    }

    return this.groupEnrollmentGQLService
      .getGroupEnrollments(params, filteredUserGroupsIdList)
      .pipe(
        map((result) => {
          const groupEnrollments: IGroupEnrollmentDTO[] = result.nodes.map(
            (x) => {
              return {
                ...x,
                isExpanded: true,
              };
            },
          );

          return {
            groupEnrollments,
            pageInfo: result.pageInfo,
          };
        }),
        catchError((error: unknown) => {
          if (error) {
            this.alertService.error(error as HttpErrorResponse | string);
          }
          return of(null);
        }),
      );
  }

  async createGroupEnrollment(groupEnrollment: IGroupEnrollment) {
    const modal = this.modal.open(AddGroupEnrollmentsModalComponent, {
      ...DEFAULT_MODAL_OPTIONS,
      size: 'md',
    });
    const modalInstance =
      modal.componentInstance as AddGroupEnrollmentsModalComponent;

    modalInstance.groupEnrollment = groupEnrollment;

    await modal.result;

    this.groupEnrollments = [];
    this.groupEnrollmentStateService.reload();
  }

  createIndenpendantGroupEnrollment() {
    this.usersService.getUserAllowedGroups().subscribe({
      next: (groups) => {
        const modal = this.modal.open(
          AddIndependantGroupEnrollmentsModalComponent,
          { ...DEFAULT_MODAL_OPTIONS, size: 'md' },
        );
        const modalInstance =
          modal.componentInstance as AddIndependantGroupEnrollmentsModalComponent;

        modalInstance.userGroups = groups;

        modal.result
          .then(() => {
            this.groupEnrollments = [];
            this.groupEnrollmentStateService.reload();
          })
          .catch((error: unknown) => {
            if (error) {
              this.alertService.error(error as HttpErrorResponse | string);
            }
          });
      },
      error: (error) => {
        if (error.status == 401) {
          this.alertService.error(
            'Error: The current user is not authorized to access this resource.',
          );
        } else {
          console.log(error);
          this.alertService.error('Something went wrong. Please try again.');
        }
      },
    });
  }

  openViewModifyEnrollment(groupEnrollment: IGroupEnrollment) {
    const modal = this.modal.open(ViewModifyEnrollmentsModalComponent, {
      ...DEFAULT_MODAL_OPTIONS,
      size: 'md',
    });
    const modalInstance =
      modal.componentInstance as ViewModifyEnrollmentsModalComponent;

    modalInstance.seqNo = groupEnrollment.seqNo;

    modal.result
      .then(() => {
        this.groupEnrollments = [];
        this.groupEnrollmentStateService.reload();
      })
      .catch((error: unknown) => {
        if (error) {
          this.alertService.error(error as HttpErrorResponse | string);
        }
      });
  }

  editNote(groupEnrollment: IGroupEnrollment) {
    const modal = this.modal.open(EditGroupEnrollmentNoteModalComponent, {
      ...DEFAULT_MODAL_OPTIONS,
      size: 'sm',
    });
    const modalInstance =
      modal.componentInstance as EditGroupEnrollmentNoteModalComponent;

    modalInstance.groupEnrollment = groupEnrollment;
    modal.result
      .then(() => {
        this.groupEnrollmentStateService.reload();
      })
      .catch((error: unknown) => {
        if (error) {
          this.alertService.error(error as HttpErrorResponse | string);
        }
      });
  }

  onGroupEnrollmentStatusChanges(groupEnrollment: IGroupEnrollmentUpdate) {
    this.subscriptions.add(
      this.groupEnrollmentService
        .editGroupEnrollment(groupEnrollment)
        .subscribe({
          next: () => {
            const successMessage = `Status for ${groupEnrollment.seqNo} has been updated successfully!`;
            this.alertService.success(successMessage);
          },
          error: (error: unknown) => {
            if (error) {
              this.alertService.error(error as HttpErrorResponse | string);
            }
          },
        }),
    );
  }

  editCaseNumber(groupEnrollment: IGroupEnrollment) {
    const modal = this.modal.open(EditGroupEnrollmentCasenumberComponent, {
      ...DEFAULT_MODAL_OPTIONS,
      size: 'sm',
    });
    const modalInstance =
      modal.componentInstance as EditGroupEnrollmentCasenumberComponent;

    modalInstance.groupEnrollment = groupEnrollment;
    modal.result
      .then(() => {
        this.groupEnrollmentStateService.reload();
      })
      .catch((error: unknown) => {
        if (error) {
          this.alertService.error(error as HttpErrorResponse | string);
        }
      });
  }

  onScroll() {
    this.groupEnrollmentStateService.onScroll();
  }

  onSort(sortBy: ISortEventArgs) {
    this.groupEnrollmentStateService.sort(sortBy);
  }

  onApplyFilter(data: GroupEnrollmentFilters) {
    this.groupEnrollmentStateService.filter = data;
  }

  toggleFiltersPane() {
    this.isFiltersExpanded =
      this.groupEnrollmentStateService.isFiltersExpanded =
        !this.groupEnrollmentStateService.isFiltersExpanded;
  }

  get moreRowsExists() {
    return this.groupEnrollmentStateService.moreRowsExists;
  }

  set moreRowsExists(value: boolean) {
    this.groupEnrollmentStateService.moreRowsExists = value;
  }

  get sortDirections(): TSortDirections {
    return this.groupEnrollmentStateService.sortDirections;
  }

  private getGroupEnrollmentFiltered(
    groupEnrollments: IGroupEnrollmentDTO[],
  ): IGroupEnrollmentDTO[] {
    const results = groupEnrollments.reduce((results, group) => {
      (results[group.id] = results[group.id] || []).push(group);
      return results;
    }, {});

    const groupEnrollmentsFiltred = Object.keys(results).map((key) => {
      const groupsBasedOnEnrollmentId = results[key]; //array of items
      const element = groupsBasedOnEnrollmentId.shift();

      element.subItems = groupsBasedOnEnrollmentId;
      return element;
    });

    return groupEnrollmentsFiltred;
  }
}

export type IGroupEnrollmentDTO = {
  subItems?: IGroupEnrollment[];
  isExpanded: boolean;
} & IGroupEnrollment;
